import React, {
  useCallback, useEffect, useRef, useContext, useState,
} from 'react';
import { useRouteMatch } from 'react-router';
import { push } from 'connected-react-router';
import { useDispatch, useSelector } from 'react-redux';
import copyToClipboard from 'copy-to-clipboard';

import RouteGetter from '../../../routes/RouteGetter';
import { ButtonGroup } from '../../../components';
import UploadFilesAction from './UploadFilesAction';
import { selectCurrentUser } from '../../../store/modules/users/selectors';
import { TabLinkService } from '../../../services/TabLinkService/TabLinkService';
import { Project } from '../../../types/items';
import { selectArticleComment } from '../../../store/modules/articleComments/selectors';
import { getProjectInfo } from './CopyProjectInfoAction';
import { notify } from '../../../store/modules/notifications/actions';
import { DeferredActionContext } from '../../../services/common/DeferredActionContext';
import { uploadMediaFromURLInitial } from '../../../store/modules/articles/actions';
import UploadFromUrlAction from './UploadFromUrlAction';
import { UploadFromUrlForm } from '../../../store/modules/articles/types';
import {
  CopyIcon,
  DesktopIcon,
  FolderPlusIcon,
  InfoIcon,
  LinkIcon,
  PlusCircleIcon
} from '../../../components/icon/solid';
import { ButtonOption } from '../../../components/button/ButtonGroup';
import { getAllQueryData } from '../../../utility/helpers';
import DownloadAllMediaAction from "./DownloadAllMediaAction";

type Props = {
  project: Project;
};

const GroupActions = ({ project }: Props) => {
  const { id: projectId } = project;
  const refInput = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();
  const { data: currentUser } = useSelector(selectCurrentUser);
  const { path, params, url } = useRouteMatch<{ id: string }>();
  const canEdit = project?.permissions.can_edit;
  const { isFocused: isFocusedArticleCommentField } = useSelector(selectArticleComment);
  const { addAction } = useContext(DeferredActionContext);

  const [openUploadFromUrl, setOpenUploadFromUrl] = useState(false);
  const [openDownloadAll, setOpenDownloadAll] = useState(false);

  const uploadHandler = useCallback(() => {
    const to = RouteGetter.get('project').media(params.id);
    if (to !== url) {
      const projectTabs = TabLinkService.get(
        'projects',
        { recordId: params.id, path, currentUser },
      );
      const index = projectTabs.findIndex((item) => item.to === to);
      if (index !== -1) {
        dispatch(push(to));
      }
      addAction(
        () => {
          if (refInput.current) {
            refInput.current.click();
          }
        },
      );
    } else if (refInput.current) {
      refInput.current.click();
    }
  }, [refInput, dispatch, params, url, currentUser, path, addAction]);

  const toggleDownloadAll = useCallback(()=> setOpenDownloadAll(!openDownloadAll), [openDownloadAll]);

  const uploadFromUrlHandler = useCallback(() => {
    const formInitial: UploadFromUrlForm = {
      title: '',
      url: '',
      project_id: projectId,
    };
    dispatch(uploadMediaFromURLInitial(formInitial));

    setOpenUploadFromUrl(true);
  }, [setOpenUploadFromUrl, projectId, dispatch]);

  const studioHandler = useCallback((
    event: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>,
    newTab: boolean = false,
  ) => {
    event.stopPropagation();

    const queryParams = getAllQueryData();
    const studioParams = queryParams?.scriptId ? `?scriptId=${queryParams.scriptId}` : '';

    if (newTab) {
      dispatch(push(RouteGetter.get('project').scripts(params.id) + studioParams));
      window.open(RouteGetter.get('project').studio(params.id) + studioParams);
    } else if (event.ctrlKey) {
      dispatch(push(RouteGetter.get('project').scripts(params.id) + studioParams));
      window.open(RouteGetter.get('project').studio(params.id) + studioParams);
    } else {
      dispatch(push(RouteGetter.get('project').studio(params.id) + studioParams));
    }
  }, [params, dispatch]);

  const copyProjectInfoHandler = useCallback(() => {
    const projectInfo = getProjectInfo(project);

    if (copyToClipboard(projectInfo, { format: 'text/plain' })) {
      dispatch(notify({
        type: 'success',
        icon: 'copy',
        message: 'Project info copied.',
      }));
    } else {
      dispatch(notify({
        type: 'error',
        icon: 'copy',
        message: 'Copying failed.',
      }));
    }
  }, [dispatch, project]);

  const buttons: ButtonOption[] = [
    {
      title: 'Studio Mode',
      icon: <DesktopIcon />,
      onClick: (e: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => {
        studioHandler(e);
      },
      disabled: false,
      path: [
        RouteGetter.get('project').scriptsPath(),
        RouteGetter.get('project').activityPath(),
        RouteGetter.get('project').adminPath(),
        RouteGetter.get('project').overviewPath(),
        RouteGetter.get('project').ordersPath(),
      ],
    },
    {
      title: 'Studio Mode (new tab)',
      icon: <FolderPlusIcon />,
      onClick: (e: React.MouseEvent<HTMLLIElement | HTMLButtonElement, MouseEvent>) => {
        studioHandler(e, true);
      },
      disabled: false,
      path: [],
      withSeparator: true,
    },
  ];

  if (canEdit) {
    buttons.push({
      title: 'Upload Media',
      icon: <PlusCircleIcon />,
      onClick: uploadHandler,
      disabled: false,
      path: [
        RouteGetter.get('project').mediaPath(),
      ],
    });
    if (currentUser?.is_super_admin) {
      buttons.push({
        title: 'Upload video from URL',
        icon: <LinkIcon />,
        onClick: uploadFromUrlHandler,
        disabled: false,
        path: [
          RouteGetter.get('project').mediaPath(),
        ],
      });
    }
  }

  buttons.push({
    title: 'Download All Media',
    icon: <CopyIcon />,
    onClick: toggleDownloadAll,
    disabled: false,
    path: [],
    withSeparator: true,
  });

  if (currentUser?.is_super_admin) {
    buttons.push({
      title: 'Copy project info',
      icon: <InfoIcon />,
      onClick: copyProjectInfoHandler,
      disabled: false,
      path: [],
    });
  }

  const selectDefault = useCallback(() => buttons
    .findIndex((item) => item.path.includes(path)), [path, buttons]);

  useEffect(() => {
    const hotKeyStudioMode = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'Enter' && !isFocusedArticleCommentField) {
        event.preventDefault();
        dispatch(push(RouteGetter.get('project').studio(params.id)));
      }
    };
    window.addEventListener('keydown', hotKeyStudioMode);

    return () => {
      window.removeEventListener('keydown', hotKeyStudioMode);
    };
  }, [dispatch, params, isFocusedArticleCommentField]);

  if (path === RouteGetter.get('project').orderPath()) {
    return null;
  }

  return (
    <>
      <DownloadAllMediaAction open={openDownloadAll} closeHandler={toggleDownloadAll} />
      <UploadFilesAction refInput={refInput} />
      <UploadFromUrlAction
        open={openUploadFromUrl}
        closeHandler={() => setOpenUploadFromUrl(false)}
      />
      <ButtonGroup
        buttons={buttons}
        selectedDefault={selectDefault}
        disableUpdateMainButton={false}
      />
    </>
  );
};

export default GroupActions;
